/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  startBulkAdmissionJob,
  getFormListsForAdminFilters,
  rejectApplicationViaAdmin,
  downloadApplicationsExcel,
  getAllBatchesViaAdmin,
} from "../../../utils/Api";
import AdmitStudentsDialog from "./AdmitStudentsDialog";
import { NavLink, useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import CreateIcon from "@mui/icons-material/Create";
import DataTable from "react-data-table-component";
import PanelFormListFilters from "./PanelFormListFilters";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { Button } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const PAGE_SIZE = 10;
const DENY_DISABLED_STATUSES = new Set([
  "student admitted",
  "rejected",
  "cancelled",
]);
const EDIT_DISABLED_STATUSES = new Set(DENY_DISABLED_STATUSES);

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const humanizeEligibilityCode = (code) =>
  String(code || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/^./, (character) => character.toUpperCase());

const getAdmissionEligibility = (row) => {
  const backendEligibility = row?.admissionEligibility;

  if (typeof backendEligibility?.eligible === "boolean") {
    return {
      eligible: backendEligibility.eligible,
      code: backendEligibility.code,
      reason:
        backendEligibility.reason ||
        (!backendEligibility.eligible
          ? humanizeEligibilityCode(backendEligibility.code) ||
            "This application is not eligible for admission."
          : null),
    };
  }

  if (row?.retired === true) {
    return {
      eligible: false,
      code: "APPLICATION_RETIRED",
      reason: "This application is retired and cannot be admitted.",
    };
  }

  const stageEligible = Number(row?.stage) === 3;
  const statusEligible =
    normalizeStatus(row?.formStatusFromAdmin) === "completed by student";
  const paymentStatus = normalizeStatus(row?.paymentStatus);
  const paymentEligible =
    paymentStatus === "paid" || paymentStatus === "partial";

  if (!stageEligible) {
    return {
      eligible: false,
      code: "INCOMPLETE_STAGE",
      reason: "Application must be at stage 3 before admission.",
    };
  }
  if (!statusEligible) {
    return {
      eligible: false,
      code: "FORM_STATUS_INELIGIBLE",
      reason: "Application status must be Completed By Student.",
    };
  }
  if (!paymentEligible) {
    return {
      eligible: false,
      code:
        paymentStatus === "unpaid"
          ? "PAYMENT_REQUIRED"
          : "PAYMENT_STATUS_INELIGIBLE",
      reason: "Payment status must be paid or partial.",
    };
  }

  return { eligible: true, code: "ELIGIBLE", reason: null };
};

const canSelectForAdmission = (row) => {
  const eligibility = getAdmissionEligibility(row);
  return eligibility.eligible || eligibility.code === "PAYMENT_REQUIRED";
};

const statusBadgeClass = (status) => {
  switch (normalizeStatus(status)) {
    case "student admitted":
      return "badge-success";
    case "completed by student":
      return "badge-info";
    case "rejected":
    case "cancelled":
      return "badge-danger";
    case "pending":
    case "incomplete":
      return "badge-warning";
    default:
      return "badge-secondary";
  }
};

const formatBackendIssue = (item) => {
  if (typeof item === "string") return item;
  return item?.reason || item?.message || item?.code || "Unknown issue";
};

const getBackendErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (typeof data?.error === "string" && data.error.trim()) return data.error;
  if (typeof data?.reason === "string" && data.reason.trim())
    return data.reason;
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors.map(formatBackendIssue).filter(Boolean).join("; ");
  }
  return fallback;
};

const PanelFormList = () => {
  const [applications, setApplications] = useState([]);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalApplications, setTotalApplications] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectionRemark, setRejectionRemark] = useState("");
  const [rejectionTargetId, setRejectionTargetId] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [admissionTargetId, setAdmissionTargetId] = useState(null);
  const [openAdmitDialog, setOpenAdmitDialog] = useState(false);
  const [baseBatches, setBaseBatches] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [paginationResetToggle, setPaginationResetToggle] = useState(false);
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  const [matchingApplicationIds, setMatchingApplicationIds] = useState([]);
  const [admitting, setAdmitting] = useState(false);
  const listRequestId = useRef(0);
  const selectionRequestId = useRef(0);
  const navigate = useNavigate();

  function formatDateTime(isoString) {
    const date = new Date(isoString);

    // Extracting individual components
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    // Converting to 12-hour format
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, "0");

    return `${month}-${day}-${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
  }

  const fetchAllBatches = async () => {
    try {
      const { batches } = await getAllBatchesViaAdmin();
      const baseNames = new Set();

      const getBaseName = (name) => {
        if (!name) return "";
        const trimmedName = name.trim();
        const parts = trimmedName.split("-");
        // Check if the last part is a single letter (a section identifier)
        if (
          parts.length > 1 &&
          parts[parts.length - 1].length === 1 &&
          /^[A-Z]$/i.test(parts[parts.length - 1])
        ) {
          return parts.slice(0, -1).join("-");
        }
        return trimmedName;
      };

      batches.forEach((batch) => {
        const baseName = getBaseName(batch.batchName);
        if (baseName) {
          baseNames.add(baseName);
        }
      });
      setBaseBatches(Array.from(baseNames));
    } catch (error) {
      toast.error("Error fetching batches");
    }
  };

  const fetchAllForms = useCallback(
    async (page = 1, { includeIds = false } = {}) => {
      const requestId = ++listRequestId.current;
      setLoading(true);
      try {
        const response = await getFormListsForAdminFilters(
          { ...filters },
          { page, includeIds },
        );
        if (requestId !== listRequestId.current) return;
        setApplications(response.applications);
        setCurrentPage(response.currentPage);
        setTotalApplications(
          response.totalApplications ?? response.applications.length,
        );
        if (includeIds && response.matchingApplicationIds) {
          setMatchingApplicationIds(response.matchingApplicationIds);
        }
      } catch (error) {
        if (requestId === listRequestId.current) {
        toast.warn("Error fetching applications");
        }
      } finally {
        if (requestId === listRequestId.current) setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    fetchAllForms(currentPage);
    fetchAllBatches();
  }, [currentPage, fetchAllForms]);

  const tableCustomStyles = {
    headRow: {
      style: {
        color: "#fff",
        backgroundColor: "#0F1015",
      },
    },
    striped: {
      default: "black",
    },
  };

  const handleAdmitClick = (row) => {
    const eligibility = getAdmissionEligibility(row);
    if (!canSelectForAdmission(row)) {
      toast.info(eligibility.reason);
      return;
    }
    setAdmissionTargetId(row._id);
    setOpenAdmitDialog(true);
  };

  // Single-row and bulk admission both go through the same previewed endpoint.
  const admissionTargetIds = admissionTargetId
    ? [admissionTargetId]
    : selectAllAcrossPages
      ? matchingApplicationIds
      : selectedRows.map((row) => row._id);

  const closeAdmitDialog = () => {
    setOpenAdmitDialog(false);
    setAdmissionTargetId(null);
  };

  const clearSelections = () => {
    selectionRequestId.current += 1;
    setSelectedRows([]);
    setMatchingApplicationIds([]);
    setSelectAllAcrossPages(false);
    setAdmissionTargetId(null);
    setToggleCleared((prev) => !prev);
  };

  const admitStudent = async (options) => {
    if (admissionTargetIds.length === 0) {
      toast.info(
        "Select eligible applications via checkboxes or use the Admit action button.",
      );
      return;
    }

    setAdmitting(true);
    try {
      const res = await startBulkAdmissionJob({
        applicationIds: admissionTargetIds,
        ...options,
      });
      toast.success(res.message, {
        onClick: () => navigate(`/panel-admin/admission-jobs/${res.jobId}`),
        autoClose: 6000,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      closeAdmitDialog();
      clearSelections();
      fetchAllForms(currentPage);
    } catch (error) {
      const data = error?.response?.data;
      if (data?.blockers?.length) {
        toast.error(
          `Admission blocked: ${formatBackendIssue(data.blockers[0])}`,
          {
            autoClose: 8000,
          },
        );
      } else {
        toast.warn(
          getBackendErrorMessage(error, "Failed to start the admission job."),
        );
      }
    } finally {
      setAdmitting(false);
    }
  };

  const closeRejectDialog = () => {
    setOpenRejectDialog(false);
    setRejectionTargetId(null);
    setRejectionRemark("");
  };

  const handleReject = (id) => {
    setRejectionTargetId(id);
    setRejectionRemark("");
    setOpenRejectDialog(true);
  };

  const handleRejectSubmit = async () => {
    if (rejecting) return;
    const remark = rejectionRemark.trim();
    if (!remark) {
        toast.error("Please provide a rejection remark");
        return;
      }
    if (!rejectionTargetId) {
      toast.error("No application was selected for rejection.");
      closeRejectDialog();
      return;
    }

    setRejecting(true);
    try {
      await rejectApplicationViaAdmin(rejectionTargetId, { remark });
      clearSelections();
      closeRejectDialog();
      toast.success("Application rejected successfully");
      fetchAllForms(currentPage);
    } catch (error) {
      toast.error(
        getBackendErrorMessage(error, "Failed to reject application."),
      );
    } finally {
      setRejecting(false);
    }
  };

  const handleRowSelected = (state) => {
    if (selectAllAcrossPages) return;
    setSelectedRows(state.selectedRows);
  };

  const handleSelectAllAcrossPages = async () => {
    const requestId = ++selectionRequestId.current;
    try {
      const response = await getFormListsForAdminFilters(
        { ...filters },
        {
          page: currentPage,
          includeIds: true,
        },
      );
      if (requestId !== selectionRequestId.current) return;
      const eligibleIds = response.matchingApplicationIds;
      if (Array.isArray(eligibleIds) && eligibleIds.length > 0) {
        setMatchingApplicationIds(eligibleIds);
        setSelectAllAcrossPages(true);
        setSelectedRows([]);
        setAdmissionTargetId(null);
        setToggleCleared((prev) => !prev);
        toast.success(
          `Selected all ${eligibleIds.length} eligible applications matching the filters.`,
        );
      } else {
        clearSelections();
        toast.info("No eligible applications match the current filters.");
      }
    } catch (error) {
      if (requestId === selectionRequestId.current) {
        toast.error(
          getBackendErrorMessage(
            error,
            "Failed to select all eligible applications.",
          ),
        );
      }
    }
  };

  const openBulkAdmitDialog = () => {
    setAdmissionTargetId(null);
    setOpenAdmitDialog(true);
  };

  const handlePageChange = (page) => {
    if (!selectAllAcrossPages) clearSelections();
    setCurrentPage(page);
  };

  const columns = [
    {
      name: "SL. No",
      selector: (_row, index) => (currentPage - 1) * PAGE_SIZE + index + 1,
      width: "80px",
    },
    {
      name: "Student Name",
      selector: (row) =>
        `${row?.studentDetails?.firstName || ""} ${
          row?.studentDetails?.lastName || ""
        }`.trim(),
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Course Name",
      selector: (row) => row?.course,
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "Application Number",
      selector: (row) => row?.applicationNumber,
      sortable: true,
      width: "130px",
    },
    {
      name: "Application Status",
      selector: (row) => row?.formStatusFromAdmin || "Pending",
      cell: (row) => {
        const status = row?.formStatusFromAdmin || "Pending";
        return (
          <span className={`badge ${statusBadgeClass(status)}`}>{status}</span>
        );
      },
      sortable: true,
      minWidth: "170px",
    },
    {
      name: "Stage",
      selector: (row) => Number(row?.stage) || 0,
      cell: (row) => (
        <span className="badge badge-secondary">Stage {row?.stage ?? "—"}</span>
      ),
      sortable: true,
      width: "100px",
    },
    {
      name: "Payment",
      selector: (row) => normalizeStatus(row?.paymentStatus),
      cell: (row) => {
        const status = normalizeStatus(row?.paymentStatus);
        if (status === "paid") {
          return <span className="badge badge-success">Paid</span>;
        }
        if (status === "partial") {
          return <span className="badge badge-warning">Partial</span>;
        }
        return <span className="badge badge-danger">Unpaid</span>;
      },
      sortable: true,
      width: "110px",
    },
    {
      name: "Admission Eligibility",
      selector: (row) => getAdmissionEligibility(row).eligible,
      cell: (row) => {
        const eligibility = getAdmissionEligibility(row);
        return (
          <span
            className={`badge ${
              eligibility.eligible ? "badge-success" : "badge-danger"
            }`}
            title={eligibility.reason || "Eligible for admission"}
          >
            {eligibility.eligible ? "Eligible" : "Not eligible"}
          </span>
        );
      },
      sortable: true,
      minWidth: "145px",
    },
    {
      name: "Applied Date",
      selector: (row) => formatDateTime(row?.updatedAt),
      sortable: true,
      minWidth: "190px",
    },
    {
      name: "Actions",
      cell: (row) => {
        const eligibility = getAdmissionEligibility(row);
        const admissionSelectable = canSelectForAdmission(row);
        const normalizedStatus = normalizeStatus(row?.formStatusFromAdmin);
        const isRetired = row?.retired === true;
        const denyDisabled =
          isRetired || DENY_DISABLED_STATUSES.has(normalizedStatus);
        const editDisabled =
          isRetired || EDIT_DISABLED_STATUSES.has(normalizedStatus);
        const displayStatus = isRetired
          ? "retired"
          : row?.formStatusFromAdmin || "current status";
        const admitTitle = eligibility.eligible
          ? "Admit student"
          : admissionSelectable
            ? "Preview admission with a documented unpaid override"
            : eligibility.reason;
        const denyTitle = denyDisabled
          ? `Cannot deny an application with status ${displayStatus}.`
          : "Reject application";
        const editTitle = editDisabled
          ? `Editing is disabled for ${displayStatus} applications.`
          : "Edit application";

        return (
          <div className="flex gap-2 items-center align-middle">
            <NavLink
              className="text-info"
              title="Preview application"
              to={`/panel-admin/preview-application-form/${row._id}`}
            >
              <VisibilityIcon />
            </NavLink>
            {editDisabled ? (
              <span
                className="text-gray-400 cursor-not-allowed"
                title={editTitle}
                aria-label={editTitle}
              >
                <CreateIcon />
              </span>
            ) : (
            <NavLink
              className="text-warning"
                title={editTitle}
              to={`/panel-admin/edit-application-form/${row._id}`}
            >
              <CreateIcon />
            </NavLink>
            )}
            <NavLink
              className="text-success"
              title="Print application"
              to={`/panel-admin/print-form/${row._id}`}
            >
              <LocalPrintshopIcon htmlColor="gray" />
            </NavLink>
            <span title={admitTitle}>
            <button
              className={`focus:outline-none text-white ${
                  admissionSelectable
                    ? "bg-green-700 hover:bg-green-800"
                    : "bg-gray-400 cursor-not-allowed"
                } focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-3 py-2`}
                aria-label={admitTitle}
                onClick={() => handleAdmitClick(row)}
                disabled={!admissionSelectable}
            >
              Admit
            </button>
            </span>
            <span title={denyTitle}>
            <button
                className={`focus:outline-none text-white ${
                  denyDisabled
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-700 hover:bg-red-800"
                } focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-3 py-2`}
                aria-label={denyTitle}
              onClick={() => handleReject(row._id)}
                disabled={denyDisabled}
            >
              Deny
            </button>
            </span>
          </div>
        );
      },
      minWidth: "345px",
    },
  ];

  const handleFilterChange = (newFilters) => {
    clearSelections();
    setCurrentPage(1);
    setPaginationResetToggle((previous) => !previous);
    setFilters(newFilters);
  };
  const resetFilter = (newFilters) => {
    clearSelections();
    setCurrentPage(1);
    setPaginationResetToggle((previous) => !previous);
    setFilters(newFilters);
  };

  const handleDownload = async () => {
    try {
      const blob = await downloadApplicationsExcel(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "applications.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Excel downloaded successfully");
    } catch (error) {
      toast.error(error.message || "Failed to download Excel");
    }
  };

  return (
    <div>
      <div className="mb-5">
        <PanelFormListFilters
          onFilterChange={handleFilterChange}
          resetFilter={resetFilter}
        />
      </div>
      <div className="col-lg-12 grid-margin stretch-card">
        <div className="card">
          <div className="card-body">
            <div className="flex flex-wrap gap-3 justify-between items-center">
              <div>
                <h4 className="card-title">
                  Application Forms (Students Applications)
                </h4>
                <p className="card-description">
                  All Students forms are listed here...
                </p>
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                <p className="text-sm text-gray-600">
                  Total matching applications: {totalApplications}
                </p>
                {selectAllAcrossPages ? (
                  <span className="text-sm text-green-700 font-medium">
                    All {matchingApplicationIds.length} eligible matching
                    applications selected.
                  </span>
                ) : (
                  <span className="text-sm text-gray-600">
                    Eligible selected on this page: {selectedRows.length}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-center justify-end">
                <button
                  className="focus:outline-none text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-2 me-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                  onClick={handleDownload}
                >
                  <FileDownloadIcon className="mr-2" />
                  Download Excel
                </button>
                <button
                  className="focus:outline-none text-white bg-purple-700 hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-3 py-2 me-2 disabled:opacity-50"
                  onClick={handleSelectAllAcrossPages}
                  disabled={
                    loading || selectAllAcrossPages || totalApplications === 0
                  }
                >
                  Select All Eligible
                </button>
                {(selectAllAcrossPages || selectedRows.length > 0) && (
                  <button
                    className="focus:outline-none text-gray-700 bg-gray-200 hover:bg-gray-300 focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-3 py-2 me-2"
                    onClick={clearSelections}
                  >
                    Clear Selection
                  </button>
                )}
              </div>
              {(selectAllAcrossPages
                ? matchingApplicationIds.length > 0
                : selectedRows.length > 0) && (
                <button
                  className="focus:outline-none text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-3 py-2 me-2"
                  onClick={openBulkAdmitDialog}
                >
                  {`Bulk Admit Selected (${
                    selectAllAcrossPages
                      ? matchingApplicationIds.length
                      : selectedRows.length
                  })`}
                </button>
              )}
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                data={applications}
                keyField="_id"
                pagination
                paginationServer
                paginationPerPage={PAGE_SIZE}
                paginationRowsPerPageOptions={[PAGE_SIZE]}
                paginationTotalRows={totalApplications}
                paginationResetDefaultPage={paginationResetToggle}
                onChangePage={handlePageChange}
                progressPending={loading}
                selectableRows
                onSelectedRowsChange={handleRowSelected}
                clearSelectedRows={toggleCleared}
                selectableRowDisabled={(row) => !canSelectForAdmission(row)}
                // highlightOnHover
                // striped
                customStyles={tableCustomStyles}
              />
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={openRejectDialog}
        onClose={rejecting ? undefined : closeRejectDialog}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            minWidth: "400px",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0" }}>
          Reject Application
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "20px !important" }}>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Remark"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={rejectionRemark}
            onChange={(e) => setRejectionRemark(e.target.value)}
            placeholder="Please provide a reason for rejection..."
          />
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px",
            backgroundColor: "#f7fafc",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            onClick={closeRejectDialog}
            variant="outlined"
            disabled={rejecting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectSubmit}
            variant="contained"
            color="error"
            disabled={rejecting}
            sx={{
              backgroundColor: "#b91c1c",
              "&:hover": {
                backgroundColor: "#991b1b",
              },
            }}
          >
            {rejecting ? "Rejecting…" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
      <AdmitStudentsDialog
        open={openAdmitDialog}
        onClose={closeAdmitDialog}
        onConfirm={admitStudent}
        baseBatches={baseBatches}
        applicationIds={admissionTargetIds}
        isSingle={!!admissionTargetId}
        submitting={admitting}
      />
    </div>
  );
};

export default PanelFormList;
